import type { AxiosInstance } from 'axios';
import type { NotificationConfig } from './notifications';
import type { ApiKeyCreateRequest, ApiKeyResponse, ApiKeyUpdateRequest } from './types/generated';
import { makeAuthConfig, makeAuthHeaders } from './utils/make-auth-headers';

// typeshare emits `expires_in_days` as required; the backend reads null or absent as "never".
export type ApiKeyCreateBody = Omit<ApiKeyCreateRequest, 'expires_in_days'> & {
  expires_in_days?: number | null;
};

export interface RustyApiKeysProps {
  client: AxiosInstance;
}

export interface RustyApiKeysSpec {
  createApiKey(
    request: ApiKeyCreateBody,
    accessToken: string,
    notification?: NotificationConfig
  ): Promise<ApiKeyResponse>;
  listApiKeys(accessToken: string): Promise<ApiKeyResponse[]>;
  updateApiKey(
    id: string,
    request: ApiKeyUpdateRequest,
    accessToken: string,
    notification?: NotificationConfig
  ): Promise<{ success: boolean }>;
  deleteApiKey(id: string, accessToken: string): Promise<{ success: boolean }>;
}

export class RustyApiKeys implements RustyApiKeysSpec {
  private client: AxiosInstance;

  constructor({ client }: RustyApiKeysProps) {
    this.client = client;
  }

  async createApiKey(
    request: ApiKeyCreateBody,
    accessToken: string,
    notification?: NotificationConfig
  ): Promise<ApiKeyResponse> {
    const result = await this.client.post<ApiKeyResponse>(
      '/a/api-keys',
      request,
      makeAuthConfig(accessToken, notification)
    );
    return result.data;
  }

  async listApiKeys(accessToken: string): Promise<ApiKeyResponse[]> {
    const result = await this.client.get<ApiKeyResponse[]>('/a/api-keys', {
      headers: makeAuthHeaders(accessToken),
    });
    return result.data;
  }

  async updateApiKey(
    id: string,
    request: ApiKeyUpdateRequest,
    accessToken: string,
    notification?: NotificationConfig
  ): Promise<{ success: boolean }> {
    const result = await this.client.put<{ success: boolean }>(
      `/a/api-keys/${id}`,
      request,
      makeAuthConfig(accessToken, notification)
    );
    return result.data;
  }

  async deleteApiKey(id: string, accessToken: string): Promise<{ success: boolean }> {
    const result = await this.client.delete<{ success: boolean }>(`/a/api-keys/${id}`, {
      headers: makeAuthHeaders(accessToken),
    });
    return result.data;
  }
}
